/**
 * PRODUCTION CLIENT DASHBOARD
 * 
 * This is the sellable client-facing layer.
 * Consumes ONLY: GET /api/lhs-core/client/dashboard
 * 
 * 🔒 PROTECTED: Requires valid JWT token
 * 
 * NO internal logic exposed.
 * NO direct job manipulation.
 * NO technical fields.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { 
  Briefcase, 
  AlertTriangle, 
  CheckCircle2, 
  PoundSterling,
  Clock,
  RefreshCw,
  ChevronRight,
  TrendingUp,
  AlertCircle,
  Building2,
  LogOut,
  Lock,
  Mail
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────
// AUTH HELPERS
// HTTP-only cookies are the primary auth method.
// localStorage used only as fallback for Bearer token.
// ─────────────────────────────────────────────

// Create axios instance with credentials for cookies
const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,  // Send/receive HTTP-only cookies
});

// Legacy fallback for Bearer token
const TOKEN_KEY = "client_dashboard_token";

function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function setStoredToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearStoredToken() {
  localStorage.removeItem(TOKEN_KEY);
}

function getAuthHeaders() {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ─────────────────────────────────────────────
// SLA COLOR MAPPING (Visual indicators)
// ─────────────────────────────────────────────
const SLA_STYLES = {
  green: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
    label: "On Track"
  },
  amber: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-700",
    dot: "bg-amber-500",
    label: "Attention"
  },
  red: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
    dot: "bg-red-500",
    label: "Overdue"
  }
};

// ─────────────────────────────────────────────
// KPI CARD COMPONENT
// ─────────────────────────────────────────────
function KPICard({ title, value, icon: Icon, color = "slate", subtitle }) {
  const colors = {
    slate: "from-slate-600 to-slate-700",
    teal: "from-teal-600 to-teal-700",
    red: "from-red-600 to-red-700",
    green: "from-emerald-600 to-emerald-700",
    amber: "from-amber-600 to-amber-700"
  };

  return (
    <Card className="overflow-hidden">
      <div className={`bg-gradient-to-br ${colors[color]} p-4 text-white`}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm opacity-80">{title}</p>
            <p className="text-3xl font-bold mt-1" data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}>
              {value}
            </p>
            {subtitle && <p className="text-xs opacity-70 mt-1">{subtitle}</p>}
          </div>
          <Icon className="h-8 w-8 opacity-50" />
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────
// SLA INDICATOR DOT
// ─────────────────────────────────────────────
function SLADot({ sla }) {
  const style = SLA_STYLES[sla] || SLA_STYLES.green;
  return (
    <span 
      className={`inline-block w-3 h-3 rounded-full ${style.dot}`}
      title={style.label}
    />
  );
}

// ─────────────────────────────────────────────
// JOBS AT RISK SECTION
// ─────────────────────────────────────────────
function JobsAtRiskSection({ jobs }) {
  if (!jobs || jobs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            Jobs Requiring Attention
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-400">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-2 opacity-50 text-emerald-500" />
            <p className="text-emerald-600 font-medium">All jobs on track</p>
            <p className="text-sm">No SLA breaches detected</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-red-200">
      <CardHeader className="bg-red-50 border-b border-red-100">
        <CardTitle className="text-base flex items-center gap-2 text-red-700">
          <AlertTriangle className="h-5 w-5" />
          Jobs Requiring Attention ({jobs.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {jobs.map((job, idx) => (
            <div 
              key={job.job_ref} 
              className={`p-4 ${job.level === 'critical' ? 'bg-red-50' : 'bg-amber-50'}`}
              data-testid={`at-risk-job-${idx}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-sm">{job.job_ref}</span>
                    <Badge 
                      className={job.level === 'critical' 
                        ? 'bg-red-600 text-white' 
                        : 'bg-amber-500 text-white'}
                    >
                      {job.level === 'critical' ? 'CRITICAL' : 'WARNING'}
                    </Badge>
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    {job.site} {job.plot && `- Plot ${job.plot}`}
                  </div>
                  <div className="text-xs text-slate-500">
                    {job.status} {job.contractor && `• ${job.contractor}`}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-red-700">
                    {job.hours_overdue}h
                  </div>
                  <div className="text-xs text-slate-500">overdue</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────
// ACTIVE JOBS TABLE
// ─────────────────────────────────────────────
function ActiveJobsTable({ jobs }) {
  if (!jobs || jobs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-slate-500" />
            Active Jobs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-400">
            <Briefcase className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No active jobs</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-teal-600" />
            Active Jobs ({jobs.length})
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="active-jobs-table">
            <thead className="bg-slate-50 border-y">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Job Ref</th>
                <th className="text-left p-3 font-medium text-slate-600">Site / Plot</th>
                <th className="text-left p-3 font-medium text-slate-600">Status</th>
                <th className="text-left p-3 font-medium text-slate-600">Contractor</th>
                <th className="text-left p-3 font-medium text-slate-600">Next Action</th>
                <th className="text-center p-3 font-medium text-slate-600">SLA</th>
                <th className="text-right p-3 font-medium text-slate-600">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {jobs.map((job, idx) => (
                <tr key={job.job_ref} className="hover:bg-slate-50" data-testid={`active-job-row-${idx}`}>
                  <td className="p-3 font-mono font-medium">{job.job_ref}</td>
                  <td className="p-3">
                    <div>{job.site}</div>
                    {job.plot && <div className="text-xs text-slate-500">Plot {job.plot}</div>}
                  </td>
                  <td className="p-3">
                    <Badge className="bg-slate-100 text-slate-700 font-normal">
                      {job.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-slate-600">
                    {job.contractor || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="p-3 text-slate-600">{job.next_action}</td>
                  <td className="p-3 text-center">
                    <SLADot sla={job.sla} />
                  </td>
                  <td className="p-3 text-right font-medium">
                    {job.approved_value 
                      ? `£${job.approved_value.toLocaleString()}` 
                      : <span className="text-slate-400">—</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────
// FINANCIAL SUMMARY
// ─────────────────────────────────────────────
function FinancialSummary({ financials }) {
  if (!financials) return null;

  const variance = financials.approved_total - financials.invoiced_total;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <PoundSterling className="h-5 w-5 text-emerald-600" />
          Financial Summary
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-emerald-50 rounded-lg">
            <div className="text-2xl font-bold text-emerald-700" data-testid="approved-total">
              £{financials.approved_total?.toLocaleString() || 0}
            </div>
            <div className="text-xs text-slate-500 mt-1">Approved POs</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-700" data-testid="invoiced-total">
              £{financials.invoiced_total?.toLocaleString() || 0}
            </div>
            <div className="text-xs text-slate-500 mt-1">Invoiced</div>
          </div>
          <div className={`text-center p-4 rounded-lg ${variance >= 0 ? 'bg-slate-50' : 'bg-red-50'}`}>
            <div className={`text-2xl font-bold ${variance >= 0 ? 'text-slate-700' : 'text-red-700'}`}>
              £{Math.abs(variance).toLocaleString()}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {variance >= 0 ? 'Pending Invoices' : 'Over Budget'}
            </div>
          </div>
        </div>
        {financials.variance_flags > 0 && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <span className="text-sm text-amber-800">
              <strong>{financials.variance_flags}</strong> invoice{financials.variance_flags > 1 ? 's' : ''} with variance flags
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────
// RECENT COMPLETED
// ─────────────────────────────────────────────
function RecentCompleted({ jobs }) {
  if (!jobs || jobs.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          Recently Completed
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {jobs.map((job, idx) => (
            <div 
              key={job.job_ref} 
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
              data-testid={`completed-job-${idx}`}
            >
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                <div>
                  <div className="font-mono font-medium text-sm">{job.job_ref}</div>
                  {job.completed_at && (
                    <div className="text-xs text-slate-500">
                      {new Date(job.completed_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="font-medium text-emerald-700">
                {job.cost ? `£${job.cost.toLocaleString()}` : '—'}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────
// LOGIN SCREEN COMPONENT
// ─────────────────────────────────────────────
function ClientLoginScreen({ onLogin, loading, expiredMessage }) {
  const [token, setToken] = useState("");
  const [error, setError] = useState(expiredMessage || null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!token.trim()) {
      setError("Please enter your access token");
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Exchange token for HTTP-only cookie session
      const response = await apiClient.post('/api/lhs-core/client/login', {
        token: token.trim()
      });
      
      if (response.data.success) {
        // Also store in localStorage as fallback
        setStoredToken(token.trim());
        onLogin(response.data);
      }
    } catch (err) {
      console.error("Login failed:", err);
      const errorDetail = err.response?.data?.detail;
      
      if (err.response?.status === 401) {
        if (errorDetail?.includes("already been used")) {
          setError("This access link has already been used. Please request a new one from your account manager.");
        } else {
          setError("Invalid or expired token. Please request a new access link.");
        }
      } else {
        setError("Unable to verify token. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
            <Lock className="h-8 w-8 text-teal-600" />
          </div>
          <CardTitle className="text-2xl">Client Dashboard</CardTitle>
          <p className="text-slate-500 text-sm mt-2">
            Enter your access token to view your maintenance dashboard
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="text"
                placeholder="Paste your access token here"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="font-mono text-sm"
                data-testid="token-input"
              />
              <p className="text-xs text-slate-400 mt-2">
                Your token was sent via email or provided by your account manager
              </p>
            </div>
            
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="login-error">
                {error}
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full"
              disabled={isLoading || loading}
              data-testid="login-submit"
            >
              {(isLoading || loading) ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  Access Dashboard
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-6 pt-4 border-t text-center">
            <p className="text-xs text-slate-400">
              Need access? Contact your account manager or email support@lhs.com
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN DASHBOARD COMPONENT
// ─────────────────────────────────────────────
export default function ClientDashboardProduction() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expiredMessage, setExpiredMessage] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);

  // Check for token in URL (magic link) or existing session (cookie)
  useEffect(() => {
    const urlToken = searchParams.get("token");
    
    if (urlToken) {
      // Magic link - exchange for session cookie
      exchangeTokenForSession(urlToken);
    } else {
      // Check if existing session cookie is valid
      verifyExistingSession();
    }
  }, [searchParams]);

  const exchangeTokenForSession = async (token) => {
    try {
      // Exchange magic link token for HTTP-only cookie session
      const response = await apiClient.post('/api/lhs-core/client/login', { token });
      
      if (response.data.success) {
        // Also store in localStorage as fallback
        setStoredToken(token);
        setClientInfo(response.data);
        setIsAuthenticated(true);
        // Remove token from URL for security
        navigate("/client-portal-v3", { replace: true });
      }
    } catch (err) {
      console.error("Magic link exchange failed:", err);
      const errorDetail = err.response?.data?.detail;
      
      if (errorDetail?.includes("already been used")) {
        setExpiredMessage("This access link has already been used. Please request a new one.");
      } else if (err.response?.status === 401) {
        setExpiredMessage("This access link has expired. Please request a new one.");
      }
      
      clearStoredToken();
      // Clean URL
      navigate("/client-portal-v3", { replace: true });
    } finally {
      setAuthChecking(false);
    }
  };

  const verifyExistingSession = async () => {
    try {
      // Try cookie-based auth first
      const response = await apiClient.get('/api/lhs-core/client/verify-token');
      
      if (response.data.valid) {
        setClientInfo(response.data);
        setIsAuthenticated(true);
      } else {
        clearStoredToken();
      }
    } catch (err) {
      // Cookie session invalid, try localStorage fallback
      const storedToken = getStoredToken();
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/api/lhs-core/client/verify-token`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          
          if (response.data.valid) {
            setClientInfo(response.data);
            setIsAuthenticated(true);
          } else {
            clearStoredToken();
          }
        } catch (e) {
          clearStoredToken();
        }
      }
    } finally {
      setAuthChecking(false);
    }
  };

  const handleLogin = (loginData) => {
    setClientInfo(loginData);
    setIsAuthenticated(true);
    setExpiredMessage(null);
  };

  const handleLogout = async () => {
    try {
      // Clear server-side cookie
      await apiClient.post('/api/lhs-core/client/logout');
    } catch (err) {
      console.error("Logout request failed:", err);
    }
    
    clearStoredToken();
    setIsAuthenticated(false);
    setData(null);
    setClientInfo(null);
    setExpiredMessage(null);
  };

  const loadDashboard = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Try cookie-based auth first, fallback to Bearer token
      let response;
      try {
        response = await apiClient.get('/api/lhs-core/client/dashboard');
      } catch (cookieErr) {
        // Fallback to Bearer token
        response = await axios.get(`${API}/api/lhs-core/client/dashboard`, {
          headers: getAuthHeaders()
        });
      }
      
      setData(response.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Dashboard load failed:", err);
      if (err.response?.status === 401) {
        // Token expired - show friendly message
        setExpiredMessage("Your session has expired. Please log in again to continue.");
        handleLogout();
      } else {
        setError("Failed to load dashboard data");
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      loadDashboard();
      
      // Auto-refresh every 60 seconds
      const interval = setInterval(loadDashboard, 60000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, loadDashboard]);

  // Auth checking state
  if (authChecking) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-10 w-10 animate-spin text-teal-600 mx-auto" />
          <p className="text-slate-500 mt-4">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show login
  if (!isAuthenticated) {
    return <ClientLoginScreen onLogin={handleLogin} loading={loading} expiredMessage={expiredMessage} />;
  }

  // Loading dashboard data
  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-10 w-10 animate-spin text-teal-600 mx-auto" />
          <p className="text-slate-500 mt-4">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Dashboard Unavailable</h2>
            <p className="text-slate-600 mb-4">{error}</p>
            <Button onClick={loadDashboard}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { kpis, jobs_at_risk, active_jobs, financials, recent_completed } = data || {};

  return (
    <div className="min-h-screen bg-slate-100" data-testid="client-dashboard-production">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <Building2 className="h-7 w-7" />
                <h1 className="text-2xl font-bold">Maintenance Dashboard</h1>
              </div>
              <p className="text-slate-300 text-sm mt-1">
                Real-time visibility into your property maintenance
                {clientInfo?.client_id && (
                  <span className="ml-2 text-teal-300">• {clientInfo.client_id}</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-4">
              {lastRefresh && (
                <span className="text-xs text-slate-400">
                  Updated {lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadDashboard}
                disabled={loading}
                className="bg-transparent border-slate-500 text-white hover:bg-slate-600"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="bg-transparent border-red-400 text-red-300 hover:bg-red-900/30"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* KPIs Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-section">
          <KPICard 
            title="Active Jobs" 
            value={kpis?.active || 0} 
            icon={Briefcase}
            color="teal"
          />
          <KPICard 
            title="At Risk" 
            value={kpis?.at_risk || 0} 
            icon={AlertTriangle}
            color={kpis?.at_risk > 0 ? "red" : "green"}
          />
          <KPICard 
            title="Completed (30d)" 
            value={kpis?.completed_30d || 0} 
            icon={CheckCircle2}
            color="green"
          />
          <KPICard 
            title="Total Spend" 
            value={`£${(kpis?.total_spend || 0).toLocaleString()}`} 
            icon={TrendingUp}
            color="slate"
          />
        </div>

        {/* Jobs at Risk - TOP PRIORITY VISIBILITY */}
        <JobsAtRiskSection jobs={jobs_at_risk} />

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Active Jobs - Main Column */}
          <div className="lg:col-span-2">
            <ActiveJobsTable jobs={active_jobs} />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <FinancialSummary financials={financials} />
            <RecentCompleted jobs={recent_completed} />
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-slate-400 pt-4 border-t">
          Powered by LHS FM System
        </div>
      </div>
    </div>
  );
}
